'use client';

import Script from 'next/script';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { theme, sectionLabel, type Locale } from '../theme';
import { Container, SectionLabel } from '../primitives';
import { Reveal } from '../reveal';
import type { Strings } from '../strings';

declare global {
  interface Window {
    landingContactTurnstileCallback?: (token: string) => void;
    landingContactTurnstileExpired?: () => void;
    turnstile?: {
      reset: (widgetId?: string) => void;
    };
  }
}

export function Contact({
  s,
  locale,
  turnstileSiteKey,
}: {
  s: Strings;
  locale: Locale;
  turnstileSiteKey: string | null;
}) {
  const startedAt = useMemo(() => Date.now(), []);
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    window.landingContactTurnstileCallback = (value: string) => setToken(value);
    window.landingContactTurnstileExpired = () => setToken('');
    return () => {
      delete window.landingContactTurnstileCallback;
      delete window.landingContactTurnstileExpired;
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!turnstileSiteKey) {
      setStatus('error');
      setStatusMessage(
        locale === 'pt'
          ? 'Protecao anti-spam ainda nao configurada.'
          : 'Anti-spam protection is not configured yet.',
      );
      return;
    }
    if (!token) {
      setStatus('error');
      setStatusMessage(
        locale === 'pt'
          ? 'Confirma a verificacao antes de enviar.'
          : 'Complete the verification before sending.',
      );
      return;
    }

    const form = new FormData(e.currentTarget);
    setStatus('loading');
    setStatusMessage(null);

    const response = await fetch('/api/marketing-contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        startedAt,
        locale,
        name: form.get('name'),
        business: form.get('business'),
        email: form.get('email'),
        need: form.get('need'),
        budget: form.get('budget'),
        message: form.get('message'),
        website: form.get('website'),
      }),
    });

    if (!response.ok) {
      window.turnstile?.reset();
      setToken('');
      setStatus('error');
      setStatusMessage(
        locale === 'pt'
          ? 'Nao conseguimos enviar. Tenta novamente ou envia email para info@locusly.pt.'
          : 'We could not send it. Try again or email info@locusly.pt.',
      );
      return;
    }

    setStatus('sent');
    setStatusMessage(s.contact.formThanks);
  }

  return (
    <section
      id="contact"
      style={{
        padding: '120px 0',
        borderTop: `1px solid ${theme.c.border}`,
      }}
    >
      {turnstileSiteKey ? (
        <Script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js"
          strategy="afterInteractive"
        />
      ) : null}
      <Container>
        <Reveal
          className="lp-ct-head"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 48,
            marginBottom: 56,
          }}
        >
          <SectionLabel>{sectionLabel(s.contact.label)}</SectionLabel>
          <div>
            <h2
              style={{
                fontFamily: theme.fonts.display,
                fontSize: 'clamp(36px, 4.5vw, 64px)',
                fontWeight: 600,
                letterSpacing: -1.5,
                lineHeight: 1.02,
                color: theme.c.fg,
                margin: 0,
              }}
            >
              {s.contact.title}
            </h2>
            <p
              style={{
                marginTop: 20,
                fontFamily: theme.fonts.body,
                fontSize: 17,
                lineHeight: 1.55,
                color: theme.c.fgMuted,
                maxWidth: 720,
              }}
            >
              {s.contact.sub}
            </p>
          </div>
        </Reveal>

        <Reveal
          delay={140}
          className="lp-ct-card"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.6fr',
            gap: 32,
            background: theme.c.surface,
            border: `1px solid ${theme.c.border}`,
            borderRadius: theme.radii.xl,
            overflow: 'hidden',
          }}
        >
          {/* Left side -contact details */}
          <aside
            style={{
              background: theme.c.fg,
              color: theme.c.bg,
              padding: '40px 36px',
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: theme.fonts.serif,
                fontStyle: 'italic',
                fontSize: 22,
                lineHeight: 1.35,
                color: theme.c.bg,
              }}
            >
              {s.contact.intro}
            </p>

            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
                marginTop: 12,
              }}
            >
              {s.contact.lines.map((l, i) => (
                <li key={i}>
                  <div
                    style={{
                      fontFamily: theme.fonts.mono,
                      fontSize: 10,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      color: theme.c.fgFaint,
                      marginBottom: 4,
                    }}
                  >
                    {l.k}
                  </div>
                  {l.k === 'Email' || l.k === 'Telefone' || l.k === 'Phone' ? (
                    <a
                      href={l.k === 'Email' ? `mailto:${l.v}` : `tel:${l.v.replace(/\s/g, '')}`}
                      style={{
                        fontFamily: theme.fonts.display,
                        fontSize: 18,
                        fontWeight: 500,
                        color: theme.c.bg,
                        textDecoration: 'none',
                        letterSpacing: -0.3,
                      }}
                    >
                      {l.v}
                    </a>
                  ) : (
                    <div
                      style={{
                        fontFamily: theme.fonts.display,
                        fontSize: 18,
                        fontWeight: 500,
                        color: theme.c.bg,
                        letterSpacing: -0.3,
                      }}
                    >
                      {l.v}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div
              style={{
                marginTop: 'auto',
                paddingTop: 24,
                borderTop: '1px solid rgba(255,255,255,0.10)',
                fontFamily: theme.fonts.mono,
                fontSize: 11,
                letterSpacing: 1.5,
                color: theme.c.accentSoft,
              }}
            >
              {s.contact.formNoSpam}
            </div>
          </aside>

          {/* Right side -form */}
          <form
            onSubmit={handleSubmit}
            data-locusly-event="lead_submitted"
            data-locusly-meta-source="contact_form"
            style={{
              padding: '40px 40px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 18,
            }}
          >
            <input
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: -10000, width: 1, height: 1 }}
            />
            <FormField label={s.contact.formName}>
              <input
                name="name"
                type="text"
                required
                autoComplete="name"
                className="lp-input" style={inputStyle}
                placeholder=""
              />
            </FormField>
            <FormField label={s.contact.formBusiness}>
              <input
                name="business"
                type="text"
                required
                autoComplete="organization"
                className="lp-input"
                style={inputStyle}
              />
            </FormField>
            <FormField label={s.contact.formEmail} fullWidth>
              <input
                name="email"
                type="email"
                required
                autoComplete="email"
                className="lp-input" style={inputStyle}
              />
            </FormField>
            <FormField label={s.contact.formNeed}>
              <select name="need" className="lp-input" style={inputStyle} required defaultValue="">
                <option value="" disabled>
                  ···
                </option>
                {s.contact.formNeedOpts.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </FormField>
            <FormField label={s.contact.formBudget}>
              <select name="budget" className="lp-input" style={inputStyle} required defaultValue="">
                <option value="" disabled>
                  ···
                </option>
                {s.contact.formBudgetOpts.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </FormField>
            <FormField label={s.contact.formMessage} fullWidth>
              <textarea
                name="message"
                rows={5}
                className="lp-input"
                style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
                placeholder={s.contact.formMessagePh}
              />
            </FormField>

            <div style={{ gridColumn: '1 / -1', minHeight: 65 }}>
              {turnstileSiteKey ? (
                <div
                  className="cf-turnstile"
                  data-sitekey={turnstileSiteKey}
                  data-callback="landingContactTurnstileCallback"
                  data-expired-callback="landingContactTurnstileExpired"
                />
              ) : null}
            </div>

            <div
              style={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 8,
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontFamily: theme.fonts.mono,
                  fontSize: 11,
                  letterSpacing: 1.2,
                  color:
                    status === 'sent'
                      ? theme.c.success
                      : status === 'error'
                        ? theme.c.danger
                        : theme.c.fgFaint,
                }}
              >
                {statusMessage ?? s.contact.formNoSpam}
              </span>
              <button
                type="submit"
                disabled={status === 'sent' || status === 'loading'}
                className={status === 'sent' ? undefined : 'lp-btn-primary'}
                style={{
                  background: status === 'sent' ? theme.c.success : theme.c.accent,
                  color: theme.c.accentFg,
                  border: 'none',
                  fontFamily: theme.fonts.body,
                  fontSize: 15,
                  fontWeight: 500,
                  padding: '14px 26px',
                  borderRadius: theme.radii.pill,
                  cursor: status === 'sent' || status === 'loading' ? 'default' : 'pointer',
                  boxShadow: '0 12px 24px -10px rgba(200,85,61,0.45)',
                  opacity: status === 'loading' ? 0.72 : 1,
                }}
              >
                {status === 'loading'
                  ? locale === 'pt'
                    ? 'A enviar...'
                    : 'Sending...'
                  : status === 'sent'
                    ? s.contact.formSent
                    : s.contact.formSend}
              </button>
            </div>
          </form>
        </Reveal>
      </Container>

      <style>{`
        @media (max-width: 960px) {
          .lp-ct-head { grid-template-columns: 1fr !important; gap: 16px !important; }
          .lp-ct-card { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: theme.c.bgAlt,
  border: `1px solid ${theme.c.border}`,
  borderRadius: theme.radii.md,
  fontFamily: theme.fonts.body,
  fontSize: 14,
  color: theme.c.fg,
  outline: 'none',
};

function FormField({
  label,
  children,
  fullWidth = false,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        gridColumn: fullWidth ? '1 / -1' : 'auto',
      }}
    >
      <span
        style={{
          fontFamily: theme.fonts.mono,
          fontSize: 10,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          color: theme.c.fgFaint,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
